// NOTICE: KAPTION-INTEGRATION
// Composable for managing per-conversation AI feature preferences
// Stores preferences in localStorage with accountId and conversationId in keys

import { ref, computed, watch, unref, isRef } from 'vue';
import { useAccount } from './useAccount';
// NOTICE: KAPTION-INTEGRATION - usage-analytics.TRANSLATION.1
import { trackUsage } from './useUsageAnalytics';
import { USAGE_ANALYTICS_EVENTS } from 'dashboard/helper/usageAnalytics/constants';

/**
 * Composable for managing AI feature preferences per conversation
 *
 * @param {number|import('vue').Ref<number>|import('vue').ComputedRef<number>} conversationId - The conversation ID (can be reactive)
 * @returns {Object} Object containing reactive state and setter methods
 *
 * @example
 * import { useKaptionAIFeaturesPreferences } from 'dashboard/composables/useKaptionAIFeaturesPreferences';
 *
 * const { translationEnabled, suggestionsEnabled, setTranslation, setSuggestions } =
 *   useKaptionAIFeaturesPreferences(conversationId);
 */
export function useKaptionAIFeaturesPreferences(conversationId) {
  const { accountId } = useAccount();

  // Handle reactive conversationId - properly unwrap refs/computed
  const convId = computed(() => {
    // If it's a ref or computed, unwrap it; otherwise use the value directly
    return isRef(conversationId) || typeof conversationId === 'object'
      ? unref(conversationId)
      : conversationId;
  });

  // Storage keys (both include accountId)
  const translationKey = computed(() => {
    if (!convId.value || !accountId.value) return null;
    return `kaption-translation-${accountId.value}_${convId.value}`;
  });

  const suggestionsKey = computed(() => {
    if (!convId.value || !accountId.value) return null;
    return `kaption-suggestions-${accountId.value}_${convId.value}`;
  });

  // State
  const translationEnabled = ref(true);
  const suggestionsEnabled = ref(true);

  // Load from localStorage
  const loadFromStorage = () => {
    if (translationKey.value) {
      try {
        const stored = localStorage.getItem(translationKey.value);
        translationEnabled.value = stored ? JSON.parse(stored) : true;
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(
          '[useKaptionAIFeaturesPreferences] Error loading translation preference:',
          error
        );
        translationEnabled.value = true;
      }
    }

    if (suggestionsKey.value) {
      try {
        const stored = localStorage.getItem(suggestionsKey.value);
        suggestionsEnabled.value = stored ? JSON.parse(stored) : true;
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(
          '[useKaptionAIFeaturesPreferences] Error loading suggestions preference:',
          error
        );
        suggestionsEnabled.value = true;
      }
    }
  };

  // Save to localStorage
  const setTranslation = enabled => {
    if (translationKey.value) {
      translationEnabled.value = enabled;
      // NOTICE: KAPTION-INTEGRATION - usage-analytics.TRANSLATION.1
      // Single choke-point for the per-conversation live-translation toggle
      // (all 5 UI toggle components call this). Best-effort; inert when flag off.
      trackUsage(USAGE_ANALYTICS_EVENTS.LIVE_TRANSLATION_TOGGLED, {
        scope: 'conversation',
        enabled: Boolean(enabled),
      });
      try {
        localStorage.setItem(translationKey.value, JSON.stringify(enabled));
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(
          '[useKaptionAIFeaturesPreferences] Error saving translation preference:',
          error
        );
      }
    }
  };

  const setSuggestions = enabled => {
    if (suggestionsKey.value) {
      suggestionsEnabled.value = enabled;
      try {
        localStorage.setItem(suggestionsKey.value, JSON.stringify(enabled));
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(
          '[useKaptionAIFeaturesPreferences] Error saving suggestions preference:',
          error
        );
      }
    }
  };

  // Watch for conversationId or accountId changes
  // Also watch convId directly to ensure we catch prop changes
  watch([convId, accountId, translationKey, suggestionsKey], loadFromStorage, {
    immediate: true,
    flush: 'sync', // Ensure immediate updates when switching conversations
  });

  return {
    translationEnabled,
    suggestionsEnabled,
    setTranslation,
    setSuggestions,
  };
}
